import { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { Box, Button, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material'

import {
  bucketGroupRows,
  BucketedGroupRows,
  EXECUTION_DETAIL_GROUPS,
  groupTagsByDimension,
  GroupedTagComparison,
  HEADLINER_GROUPS,
  TAG_GROUP_ORDER,
} from '../../helpers/creativeTagPerformance'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { GameCreativeDetailsData } from '../../types'
import { creativeTagAnchorId, DiagnosisColumn, UnavailableDiagnosisCard } from '../CreativeTagDiagnosisPrimitives/CreativeTagDiagnosisPrimitives'
import { SectionCard } from '../SectionCard/SectionCard'

const COLLAPSED_PER_COLUMN = 5

/** Gap (px) left above a scrolled-to tag card so the sticky tab bar never covers it. */
const SCROLL_TARGET_OFFSET = 88

/** Max animation frames to wait for a scroll-target card to mount before giving up. */
const MAX_SCROLL_ATTEMPTS = 60

interface CreativeTagsByGroupSectionProps {
  source: GameCreativeDetailsData
  target: GameCreativeDetailsData
  /**
   * Scope-wide leader install share used as the 100-point anchor for every
   * per-tag Ad Performance score, so per-tag perf% stays stable across compare
   * selections instead of drifting with whichever two games are on screen.
   */
  scopeMaxInstallShare: number
  /**
   * Id of the tag to scroll to once the tab mounts — set when the user clicks a
   * tag in the Overview's Creative Tags preview. The section locates the
   * tag's group, force-expands it (and the execution-detail disclosure if
   * needed) so the card is in the DOM, then scrolls the dialog body to it.
   */
  scrollToTag?: string | null
  /** Dialog scroll container used to scroll the target tag into view. */
  scrollContainerRef?: RefObject<HTMLElement | null>
  /** Invoked once the scroll attempt completes so the parent clears `scrollToTag`. */
  onScrolledToTag?: () => void
}

/**
 * Stage 1 of the redesigned Creative Tags tab: a vertical stack of per-tag-
 * group section cards. Each card renders the same three-bucket layout the
 * old `TagComparisonDiagnosis` introduced (Shared / Source-only / Target-
 * only), but scoped to a single tag group (e.g. only narrative-function
 * tags). Within each bucket, tags are ranked by mean SoV across their
 * carrier ads (see `creativeTagPerformance.ts`), so the user reads each
 * group as "the best-performing tags in this dimension for each game."
 *
 * Layout strategy:
 *   - Headliner groups (`HEADLINER_GROUPS`) render expanded by default — the
 *     narrative/value/emotion/etc. groups UA managers care about most.
 *   - Execution-detail groups (`EXECUTION_DETAIL_GROUPS`) live behind a
 *     single bottom-of-tab "Show execution detail" toggle, so the headline
 *     view stays scannable but the long tail is one click away.
 *   - Each group has its own per-card "Show all/less" toggle that flips
 *     between `COLLAPSED_PER_COLUMN` and unbounded card counts; the toggle
 *     only shows when the cap would actually truncate something.
 */
export const CreativeTagsByGroupSection: FC<CreativeTagsByGroupSectionProps> = ({
  source,
  target,
  scopeMaxInstallShare,
  scrollToTag,
  scrollContainerRef,
  onScrolledToTag,
}) => {
  const { t } = useTranslation('ua-insights')
  const { dimensionLabel, dimensionCode } = useUATaxonomy()
  const [executionExpanded, setExecutionExpanded] = useState(false)
  // Dimension we've been asked to fully expand so a cross-tab scroll target's
  // card is rendered even when it sits past the per-group collapse cap.
  const [forceExpandDimension, setForceExpandDimension] = useState<string | null>(null)
  // Keep the latest "scroll handled" callback in a ref so the scroll effect
  // doesn't re-run (and cancel its in-flight rAF) every time the parent passes
  // a fresh inline callback identity.
  const onScrolledRef = useRef(onScrolledToTag)
  onScrolledRef.current = onScrolledToTag

  // Cross-game grouped rows live here so every per-group section reads from
  // the same memoised aggregate; the helper takes care of dimension-based
  // partitioning, so we don't reaggregate on every section render.
  const byDimension = useMemo(
    () => groupTagsByDimension(source, target, scopeMaxInstallShare, dimensionCode),
    [source, target, scopeMaxInstallShare, dimensionCode]
  )

  // Bucketise once per dimension so each section just slices `shared/sourceOnly/targetOnly`.
  const bucketedByDimension = useMemo(() => {
    const map = new Map<string, BucketedGroupRows>()
    for (const [dimension, rows] of byDimension.entries()) {
      map.set(dimension, bucketGroupRows(rows))
    }
    return map
  }, [byDimension])

  // Skip groups that contributed no rows above the carrier floor on either
  // side — listing an empty section just adds noise. Wrapped in `useCallback`
  // so the predicate is stable across renders for the `useMemo` deps below.
  const groupHasContent = useCallback(
    (dimension: string): boolean => {
      const buckets = bucketedByDimension.get(dimension)
      if (!buckets) return false
      return buckets.shared.length > 0 || buckets.sourceOnly.length > 0 || buckets.targetOnly.length > 0
    },
    [bucketedByDimension]
  )

  const headlinerDimensions = useMemo(() => HEADLINER_GROUPS.filter(groupHasContent), [groupHasContent])
  const executionDimensions = useMemo(() => EXECUTION_DETAIL_GROUPS.filter(groupHasContent), [groupHasContent])

  // Resolve the scroll target's dimension straight from the aggregated data so
  // we don't rely on the caller knowing the (possibly stale) grouping.
  const scrollTargetDimension = useMemo(() => {
    if (!scrollToTag) return null
    for (const [dimension, rows] of byDimension.entries()) {
      if (rows.some((row) => row.tag.id === scrollToTag)) return dimension
    }
    return null
  }, [scrollToTag, byDimension])

  // When asked to scroll to a tag: surface its group (expand the per-group cap
  // and the execution-detail disclosure if the tag lives there), then poll a
  // few animation frames until the card is in the DOM and scroll the dialog
  // body to it. Polling (rather than a fixed number of frames) absorbs the
  // extra commits the force-expand / disclosure state changes introduce.
  // Always clears the request via `onScrolledToTag` — even when the tag isn't
  // present in the current scope — so a one-shot scroll never gets stuck.
  useEffect(() => {
    if (!scrollToTag) return
    if (!scrollTargetDimension) {
      onScrolledRef.current?.()
      return
    }
    setForceExpandDimension(scrollTargetDimension)
    if (EXECUTION_DETAIL_GROUPS.includes(scrollTargetDimension)) setExecutionExpanded(true)

    let rafId = 0
    let attempts = 0
    const tryScroll = () => {
      const el = document.getElementById(creativeTagAnchorId(scrollToTag))
      if (el) {
        const container = scrollContainerRef?.current
        if (container) {
          const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - SCROLL_TARGET_OFFSET
          container.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
        } else {
          // Page scroll (no dialog container): the window is the scroller, but
          // the global app header and the page's sticky header occlude the top
          // of the viewport. Offset by the sticky header's live on-screen
          // bottom (it is pinned, so `bottom` is exactly where unobscured
          // content starts) plus a small gap, so the card lands just below the
          // header instead of behind it. Falls back to a fixed offset if the
          // header marker isn't found.
          const stickyHeader = document.querySelector('[data-ua-insights-sticky-header]')
          const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().bottom + 12 : SCROLL_TARGET_OFFSET
          const top = el.getBoundingClientRect().top + window.scrollY - headerOffset
          window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' })
        }
        onScrolledRef.current?.()
        return
      }
      attempts += 1
      if (attempts < MAX_SCROLL_ATTEMPTS) rafId = requestAnimationFrame(tryScroll)
      else onScrolledRef.current?.()
    }
    rafId = requestAnimationFrame(tryScroll)
    return () => cancelAnimationFrame(rafId)
  }, [scrollToTag, scrollTargetDimension, scrollContainerRef])

  // Surface any dimensions present in the data that we didn't explicitly list
  // in `TAG_GROUP_ORDER` so a freshly-introduced tag group doesn't silently
  // drop off the UI. They render after the explicit groups, alphabetised.
  const otherDimensions = useMemo(() => {
    const known = new Set(TAG_GROUP_ORDER)
    const others: string[] = []
    for (const dimension of byDimension.keys()) {
      if (!known.has(dimension) && groupHasContent(dimension)) others.push(dimension)
    }
    return others.sort((a, b) => dimensionLabel(a).localeCompare(dimensionLabel(b)))
  }, [byDimension, groupHasContent, dimensionLabel])

  const hasAnyContent = headlinerDimensions.length > 0 || executionDimensions.length > 0 || otherDimensions.length > 0

  if (source.tags.length === 0 && target.tags.length === 0) {
    return (
      <SectionCard title={t('compare_tag_section_title')}>
        <UnavailableDiagnosisCard body={t('compare_tag_diagnosis_unavailable')} />
      </SectionCard>
    )
  }

  if (!hasAnyContent) {
    // Tags exist but none meet the carrier floor — distinct from "no data
    // at all", so we surface a different (less alarming) note.
    return (
      <SectionCard title={t('compare_tag_section_title')}>
        <Typography variant="body2" color="text.secondary">
          {t('compare_tag_section_below_floor')}
        </Typography>
      </SectionCard>
    )
  }

  const executionToggleLabel = executionExpanded
    ? t('compare_tag_hide_execution_detail')
    : t('compare_tag_show_execution_detail', { count: executionDimensions.length })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('compare_tag_section_title')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {t('compare_tag_section_caption')}
        </Typography>
      </Box>

      {headlinerDimensions.map((dimension) => (
        <CreativeTagGroupSection
          key={dimension}
          dimension={dimension}
          buckets={bucketedByDimension.get(dimension)!}
          source={source}
          target={target}
          forceExpanded={forceExpandDimension === dimension}
        />
      ))}

      {executionDimensions.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setExecutionExpanded((prev) => !prev)}
              startIcon={executionExpanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
              aria-expanded={executionExpanded}
              sx={{ fontWeight: 'bold' }}
            >
              {executionToggleLabel}
            </Button>
          </Box>
          {executionExpanded &&
            executionDimensions.map((dimension) => (
              <CreativeTagGroupSection
                key={dimension}
                dimension={dimension}
                buckets={bucketedByDimension.get(dimension)!}
                source={source}
                target={target}
                forceExpanded={forceExpandDimension === dimension}
              />
            ))}
        </>
      )}

      {/* Unknown dimensions render at the very bottom regardless of the
          execution-detail toggle so newly-added groups don't get hidden by
          accident — once we explicitly slot them they move to the right
          home. */}
      {otherDimensions.map((dimension) => (
        <CreativeTagGroupSection
          key={dimension}
          dimension={dimension}
          buckets={bucketedByDimension.get(dimension)!}
          source={source}
          target={target}
          forceExpanded={forceExpandDimension === dimension}
        />
      ))}
    </Box>
  )
}

interface CreativeTagGroupSectionProps {
  dimension: string
  buckets: BucketedGroupRows
  source: GameCreativeDetailsData
  target: GameCreativeDetailsData
  /**
   * When true, render every row regardless of the per-group collapse cap so a
   * cross-tab scroll target's card is guaranteed to be in the DOM.
   */
  forceExpanded?: boolean
}

/**
 * One tag-group section card: title row (group label + total-tag count +
 * show-more toggle) over a three-column body (Shared / Source-only /
 * Target-only). Mirrors the original `TagComparisonDiagnosis`'s layout
 * primitives and palette so the new view feels like a continuation of the
 * familiar comparison vocabulary.
 */
const CreativeTagGroupSection: FC<CreativeTagGroupSectionProps> = ({ dimension, buckets, source, target, forceExpanded = false }) => {
  const { t } = useTranslation('ua-insights')
  const { dimensionLabel } = useUATaxonomy()
  const theme = useTheme()
  const [expanded, setExpanded] = useState(false)

  // A cross-tab scroll target keeps this group expanded until the user
  // collapses it again, so the linked-to card stays visible after the jump.
  useEffect(() => {
    if (forceExpanded) setExpanded(true)
  }, [forceExpanded])

  const sourceColor = theme.palette.secondary.main
  const targetColor = theme.palette.primary.main
  const sharedColor = theme.palette.success.main

  const totalTagsInGroup = buckets.shared.length + buckets.sourceOnly.length + buckets.targetOnly.length

  const canExpand =
    buckets.shared.length > COLLAPSED_PER_COLUMN || buckets.sourceOnly.length > COLLAPSED_PER_COLUMN || buckets.targetOnly.length > COLLAPSED_PER_COLUMN
  const visibleLimit = expanded ? Number.POSITIVE_INFINITY : COLLAPSED_PER_COLUMN
  const toggleLabel = expanded ? t('compare_tag_show_less') : t('compare_tag_show_more')

  const headerAction = canExpand ? (
    <Tooltip title={toggleLabel}>
      <span>
        <IconButton size="small" onClick={() => setExpanded((prev) => !prev)} aria-label={toggleLabel} aria-expanded={expanded}>
          {expanded ? <ExpandLessRounded /> : <ExpandMoreRounded />}
        </IconButton>
      </span>
    </Tooltip>
  ) : undefined

  const groupLabel = dimensionLabel(dimension)

  return (
    <SectionCard
      title={
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {groupLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            {t('compare_tag_group_total', { count: totalTagsInGroup })}
          </Typography>
        </Box>
      }
      headerAction={headerAction}
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr) auto minmax(0, 1fr)' },
        }}
      >
        <DiagnosisColumn
          bucket="shared"
          accent={sharedColor}
          title={t('compare_tag_shared_title')}
          rows={buckets.shared as GroupedTagComparison[]}
          visibleLimit={visibleLimit}
          emptyText={t('compare_tag_shared_empty')}
          sourceName={source.game.name}
          targetName={target.game.name}
          sourceColor={sourceColor}
          targetColor={targetColor}
        />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <DiagnosisColumn
          bucket="sourceOnly"
          accent={sourceColor}
          title={t('compare_tag_source_only_title', { name: source.game.name })}
          rows={buckets.sourceOnly as GroupedTagComparison[]}
          visibleLimit={visibleLimit}
          emptyText={t('compare_tag_source_only_empty', { name: source.game.name })}
          sourceName={source.game.name}
          targetName={target.game.name}
          sourceColor={sourceColor}
          targetColor={targetColor}
        />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <DiagnosisColumn
          bucket="targetOnly"
          accent={targetColor}
          title={t('compare_tag_target_only_title', { name: target.game.name })}
          rows={buckets.targetOnly as GroupedTagComparison[]}
          visibleLimit={visibleLimit}
          emptyText={t('compare_tag_target_only_empty', { name: target.game.name })}
          sourceName={source.game.name}
          targetName={target.game.name}
          sourceColor={sourceColor}
          targetColor={targetColor}
        />
      </Box>
    </SectionCard>
  )
}
